/**
 * Booking notifications (frontend-only, provider-agnostic).
 *
 * The top-bar bell now surfaces ONLY genuinely-new reservations. There is no new
 * backend subsystem: notifications are DERIVED from the existing persisted
 * reservation list ({@link BookingRow} from /api/booking/bookings/list), so a
 * booking appears exactly once it has a stable AVOS reservation id — i.e. after
 * canonical persistence. Works identically for direct and every OTA (Booking.com,
 * MakeMyTrip, Goibibo, Agoda, Expedia, Trip.com, …) because it keys off the
 * shared reservation row, never OTA-specific logic.
 *
 * DEDUPE + "new only": the stable reservation `id` is the identity. A set of
 * "seen" ids is persisted in localStorage, so webhook retry / poller recovery /
 * revision replay / duplicate OTA delivery / page refresh never re-notify (one
 * persisted reservation = one notification). A modification or cancellation does
 * NOT create a new "New booking" (same id already seen; cancelled rows are
 * excluded). Read state is tracked locally; unread badge counts unread bookings.
 */

import type { BookingRow } from "./services.js";

/** A single new-booking notification (derived, compact for the dropdown). */
export interface BookingNotification {
  /** The stable AVOS reservation id — the dedupe key + deep-link target. */
  readonly reservationId: string;
  readonly guestName: string;
  /** Guest-facing booking/confirmation reference (never a Channex revision id). */
  readonly reference: string | null;
  readonly propertyName: string | null;
  readonly unitName: string | null;
  readonly checkIn: string;
  readonly checkOut: string;
  /** Human source/channel label (e.g. "Goibibo", "Direct"). */
  readonly source: string;
  readonly amountMinor: string | null;
  readonly currency: string;
  /** When the reservation was created (ISO), for relative-time display. */
  readonly createdAt: string | null;
  readonly read: boolean;
}

const SEEN_KEY = "avos.bookingNotif.seen.v1";
const READ_KEY = "avos.bookingNotif.read.v1";
const MAX_KEPT = 30;

function readIdSet(key: string): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return new Set();
    const arr = JSON.parse(raw) as unknown;
    return new Set(Array.isArray(arr) ? arr.map(String) : []);
  } catch {
    return new Set();
  }
}

function writeIdSet(key: string, ids: Iterable<string>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify([...ids]));
  } catch {
    /* storage unavailable (private mode / quota) — degrade silently */
  }
}

/** A booking row represents a GENUINELY-NEW reservation worth notifying about. */
function isNotifiable(b: BookingRow): boolean {
  // Only persisted reservations have an id; exclude cancelled (not a "new
  // booking"). Held/confirmed both count as newly-created reservations.
  return typeof b.id === "string" && b.id !== "" && b.status !== "cancelled";
}

/** The guest-facing reference, never a Channex revision id. */
function guestReference(b: BookingRow): string | null {
  return b.confirmationCode ?? b.otaReservationCode ?? null;
}

/** A human source/channel label (provider-agnostic; reuses the row's own data). */
function sourceLabel(b: BookingRow): string {
  const raw = (b.source ?? b.channel ?? "direct").toString();
  const map: Record<string, string> = {
    direct: "Direct",
    walk_in: "Walk-in",
    manual: "Manual",
    booking_com: "Booking.com",
    makemytrip: "MakeMyTrip",
    goibibo: "Goibibo",
    agoda: "Agoda",
    expedia: "Expedia",
    trip_com: "Trip.com",
    airbnb: "Airbnb",
    other_ota: "OTA",
    ota: "OTA",
  };
  return map[raw] ?? (b.channel && b.channel !== "" ? b.channel : raw);
}

function toNotification(b: BookingRow, read: boolean): BookingNotification {
  return {
    reservationId: b.id,
    guestName: b.guestName ?? "Guest",
    reference: guestReference(b),
    propertyName: b.propertyName ?? null,
    unitName: b.unitName ?? null,
    checkIn: b.checkIn,
    checkOut: b.checkOut,
    source: sourceLabel(b),
    amountMinor: b.amountMinor ?? null,
    currency: b.currency ?? "INR",
    createdAt: b.createdAt ?? null,
    read,
  };
}

/**
 * PURE core (unit-testable, no storage): given the current booking rows and the
 * prior seen/read id sets, compute the notification list (newest first) + the
 * next seen/read sets. A row already seen keeps its read state; a never-seen
 * notifiable row becomes a new UNREAD notification. Cancelled/duplicate rows and
 * modifications of an already-seen reservation never add a new notification.
 */
export function deriveBookingNotifications(
  rows: readonly BookingRow[],
  prevSeen: ReadonlySet<string>,
  prevRead: ReadonlySet<string>,
): {
  readonly notifications: readonly BookingNotification[];
  readonly nextSeen: ReadonlySet<string>;
  readonly nextRead: ReadonlySet<string>;
} {
  const notifiable = rows.filter(isNotifiable);
  const nextSeen = new Set(prevSeen);
  const nextRead = new Set(prevRead);

  // Order newest-first by createdAt (fallback: keep input order).
  const ordered = [...notifiable].sort((a, b) => {
    const ta = a.createdAt ? Date.parse(a.createdAt) : 0;
    const tb = b.createdAt ? Date.parse(b.createdAt) : 0;
    return tb - ta;
  });

  const notifications: BookingNotification[] = [];
  for (const b of ordered) {
    nextSeen.add(b.id);
    notifications.push(toNotification(b, nextRead.has(b.id)));
  }
  // Prune seen/read sets so localStorage never grows unbounded: keep ids that
  // are still present in the current list (bounded by MAX_KEPT notifications).
  const keptIds = new Set(notifications.slice(0, MAX_KEPT).map((n) => n.reservationId));
  const prunedSeen = new Set([...nextSeen].filter((id) => keptIds.has(id)));
  const prunedRead = new Set([...nextRead].filter((id) => keptIds.has(id)));
  return {
    notifications: notifications.slice(0, MAX_KEPT),
    nextSeen: prunedSeen,
    nextRead: prunedRead,
  };
}

/** Load the current booking notifications from rows + localStorage state. */
export function loadBookingNotifications(
  rows: readonly BookingRow[],
): readonly BookingNotification[] {
  const { notifications, nextSeen, nextRead } = deriveBookingNotifications(
    rows,
    readIdSet(SEEN_KEY),
    readIdSet(READ_KEY),
  );
  writeIdSet(SEEN_KEY, nextSeen);
  writeIdSet(READ_KEY, nextRead);
  return notifications;
}

/** Mark ONE reservation's notification read (persisted). */
export function markBookingNotificationRead(reservationId: string): void {
  const read = readIdSet(READ_KEY);
  read.add(reservationId);
  writeIdSet(READ_KEY, read);
}

/** Mark ALL currently-known booking notifications read (persisted). */
export function markAllBookingNotificationsRead(
  notifications: readonly BookingNotification[],
): void {
  const read = readIdSet(READ_KEY);
  for (const n of notifications) read.add(n.reservationId);
  writeIdSet(READ_KEY, read);
}

/** Compact relative-time label for a notification timestamp. */
export function relativeTime(iso: string | null, now: number = Date.now()): string {
  if (iso === null) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const secs = Math.max(0, Math.floor((now - t) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/** Short "05 Feb" style date for the compact dropdown line. */
export function shortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  const day = String(d.getUTCDate()).padStart(2, "0");
  const month = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][d.getUTCMonth()] ?? "";
  return `${day} ${month}`;
}
